import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Film as FilmIcon } from "lucide-react";
import { api } from "../lib/api";
import SectionHeader from "../components/SectionHeader";
import VideoPlayer from "../components/VideoPlayer";
import YouTubeEmbed from "../components/YouTubeEmbed";

const FALLBACK_BANNER = "/brand/horilawa.webp";

export default function HomePage() {
  const [featured, setFeatured] = useState(null);
  const [songs, setSongs] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    document.title = "Je Ke Pictures — Cinematic Storytelling";
    Promise.all([
      api.get("/films").then(r => r.data).catch(() => []),
      api.get("/songs").then(r => r.data).catch(() => []),
      api.get("/news").then(r => r.data).catch(() => []),
    ]).then(([films, songs, news]) => {
      setFeatured(films.find(f => f.is_featured) || films[0] || null);
      setSongs(songs.slice(0, 2));
      setNews(news.slice(0, 3));
    });
  }, []);

  const banner = featured?.banner_url || featured?.poster_url || FALLBACK_BANNER;

  return (
    <div data-testid="home-page">
      {/* HERO — movie banner */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden jk-vignette" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={banner} alt={featured?.title || "Aanchra Ke Daag"} className="w-full h-full object-cover opacity-70" data-testid="hero-banner-img" />
          <div className="absolute inset-0" style={{
            background: "linear-gradient(to top, #050505 0%, rgba(5,5,5,0.55) 45%, rgba(5,5,5,0.15) 100%)"
          }}/>
        </div>
        <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24 pt-32">
          <div className="jk-fade-up">
            <div className="font-cinzel text-xs tracking-[0.5em] text-[color:var(--jk-gold)] mb-5" data-testid="hero-eyebrow">
              — JE KE PICTURES PRESENTS
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/music" className="jk-btn jk-btn-solid" data-testid="hero-watch-btn">
                <Play size={16} /> Watch Videos
              </Link>
              <Link to="/films" className="jk-btn" data-testid="hero-films-btn">
                Explore Films <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 jk-scroll-indicator">
          <div className="w-[1px] h-16 bg-gradient-to-b from-[color:var(--jk-gold)] to-transparent" />
        </div>
      </section>

      {/* FEATURED FILM DETAILS */}
      {featured && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="featured-film-section">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <div className="font-cinzel text-xs tracking-[0.4em] text-[color:var(--jk-gold)] mb-4">— FEATURED PRESENTATION</div>
              <h2 className="font-cinzel font-bold text-4xl md:text-5xl mb-6 leading-tight">The Story</h2>
              <p className="text-[color:var(--jk-muted)] leading-relaxed mb-8">{featured.synopsis}</p>
              <div className="space-y-3 text-sm">
                {featured.director && <div><span className="text-[color:var(--jk-gold)] font-cinzel tracking-wider mr-3">DIRECTOR</span><span className="text-white">{featured.director}</span></div>}
                {featured.producer && <div><span className="text-[color:var(--jk-gold)] font-cinzel tracking-wider mr-3">PRODUCER</span><span className="text-white">{featured.producer}</span></div>}
                {featured.year && <div><span className="text-[color:var(--jk-gold)] font-cinzel tracking-wider mr-3">YEAR</span><span className="text-white">{featured.year}</span></div>}
                {featured.status && <div><span className="text-[color:var(--jk-gold)] font-cinzel tracking-wider mr-3">STATUS</span><span className="uppercase text-white">{featured.status}</span></div>}
              </div>
            </div>
            <div className="md:col-span-7">
              {featured.trailer_youtube_id
                ? <YouTubeEmbed videoId={featured.trailer_youtube_id} title={featured.title} />
                : <img src={banner} alt={featured.title} className="w-full ring-1 ring-white/10" />}
            </div>
          </div>
        </section>
      )}

      {/* VIDEO PEEK */}
      {songs.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24" data-testid="music-peek-section">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <SectionHeader eyebrow="Video Releases" title="Songs from our films" />
            <Link to="/music" className="jk-link">All Videos →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {songs.map((s) => (
              <div key={s.id} className="group" data-testid={`song-card-${s.id}`}>
                <VideoPlayer src={s.video_url} poster={s.poster_url} youtubeId={s.youtube_id} title={s.title} />
                <div className="mt-4">
                  <div className="font-cinzel font-bold text-lg">{s.title_hindi || s.title}</div>
                  <div className="text-xs text-[color:var(--jk-muted)] mt-1 tracking-wider">
                    {s.singer && `Singer: ${s.singer}`}
                    {s.lyricist && ` · Lyricist: ${s.lyricist}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* NEWS PEEK */}
      {news.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24" data-testid="news-peek-section">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <SectionHeader eyebrow="Latest" title="News & Press" />
            <Link to="/news" className="jk-link">All News →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {news.map((n) => (
              <article key={n.id} className="p-6 border border-white/5 hover:border-[color:var(--jk-gold)]/40 transition-colors hover:-translate-y-1 duration-300" data-testid={`news-card-${n.id}`}>
                <div className="font-cinzel text-[0.65rem] tracking-[0.3em] text-[color:var(--jk-gold)] mb-3 uppercase">{n.category}</div>
                <h3 className="font-cinzel font-bold text-xl mb-3 leading-snug">{n.title}</h3>
                <p className="text-sm text-[color:var(--jk-muted)] leading-relaxed">{n.excerpt}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-center" data-testid="home-cta">
        <FilmIcon size={40} className="mx-auto text-[color:var(--jk-gold)] mb-6" />
        <h2 className="font-cinzel font-bold text-4xl md:text-5xl mb-6">Stories that carry a mark.</h2>
        <p className="text-[color:var(--jk-muted)] max-w-2xl mx-auto mb-8">
          Je Ke Pictures Pvt. Ltd. — committed to premium production values, storytelling excellence, and cinematic craftsmanship rooted in Bhojpuri traditions.
        </p>
        <Link to="/about" className="jk-btn" data-testid="cta-about-btn">Our Story <ArrowRight size={16}/></Link>
      </section>
    </div>
  );
}
